import React from "react";

import { Card, CardContent } from "@/components";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useI18n } from "@/context";

import {
  ReportsFilters,
  ReportsTable,
  SaleDetailsModal,
  SupplierReportsFilters,
} from "./components";
import { useReports } from "./reports.hooks";
import { useSupplierReports } from "./supplier-reports.hooks";

export const ReportsPage: React.FC = () => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = React.useState("general");

  const general = useReports(activeTab === "general");
  const supplier = useSupplierReports(activeTab === "supplier");

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      <div className="flex flex-col w-full px-2">
        <h1 className="text-3xl font-bold tracking-tight text-foreground mb-3">
          {t("reports.title")}
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t("reports.description")}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full max-w-lg">
          <TabsTrigger value="general">
            {t("reports.tabs.general")}
          </TabsTrigger>
          <TabsTrigger value="supplier">
            {t("reports.tabs.supplier")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-6 space-y-6">
          <ReportsFilters
            form={general.filtersForm}
            onApplyFilters={general.handleApplyFilters}
            onClearFilters={general.handleClearFilters}
            onExportCsv={general.handleExportCsv}
            isExporting={general.isExporting}
            statusOptions={general.statusOptions}
            partnerOptions={general.partnerOptions}
          />

          <ReportsTable
            sales={general.paginatedSales}
            hasProductBreakdown={false}
            currentPage={general.currentPage}
            totalPages={general.totalPages}
            itemsPerPage={general.itemsPerPage}
            totalItems={general.totalItems}
            onPageChange={general.handlePageChange}
            onOpenDetails={general.handleOpenDetails}
            isLoading={general.isLoading}
            isFetching={general.isFetching}
            isError={general.isError}
            error={general.error}
            isSuccess={general.isSuccess}
          />

          <SaleDetailsModal
            sale={general.selectedSale}
            isOpen={general.isDetailsModalOpen}
            onClose={general.handleCloseDetails}
          />
        </TabsContent>

        <TabsContent value="supplier" className="mt-6 space-y-6">
          <p className="text-sm text-muted-foreground px-2">
            {t("reports.supplierTab.description")}
          </p>

          <SupplierReportsFilters
            form={supplier.filtersForm}
            onApplyFilters={supplier.handleApplyFilters}
            onClearFilters={supplier.handleClearFilters}
            onExportCsv={supplier.handleExportCsv}
            isExporting={supplier.isExporting}
            statusOptions={supplier.statusOptions}
            supplierOptions={supplier.supplierOptions}
            productOptions={supplier.productOptions}
            selectedSupplierLabel={supplier.selectedSupplierLabel}
          />

          {supplier.showSelectSupplierHint ? (
            <Card className="p-4">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-muted-foreground max-w-md">
                  {t("reports.supplierTab.emptyHint")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <ReportsTable
              sales={supplier.paginatedSales}
              hasProductBreakdown
              currentPage={supplier.currentPage}
              totalPages={supplier.totalPages}
              itemsPerPage={supplier.itemsPerPage}
              totalItems={supplier.totalItems}
              onPageChange={supplier.handlePageChange}
              onOpenDetails={supplier.handleOpenDetails}
              isLoading={supplier.isLoading}
              isFetching={supplier.isFetching}
              isError={supplier.isError}
              error={supplier.error}
              isSuccess={supplier.isSuccess}
            />
          )}

          <SaleDetailsModal
            sale={supplier.selectedSale}
            isOpen={supplier.isDetailsModalOpen}
            onClose={supplier.handleCloseDetails}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
